import React from 'react';
import Alert from '@material-ui/lab/Alert';
import Autocomplete from '@material-ui/lab/Autocomplete';
import Button from '@material-ui/core/Button';
import Paper from '@material-ui/core/Paper';
import Checkbox from '@material-ui/core/Checkbox';
import Chip from '@material-ui/core/Chip';
import CircularProgress from '@material-ui/core/CircularProgress';
import FormControl from '@material-ui/core/FormControl';
import FormControlLabel from '@material-ui/core/FormControlLabel';
import FormGroup from '@material-ui/core/FormGroup';
import FormHelperText from '@material-ui/core/FormHelperText';
import FormLabel from '@material-ui/core/FormLabel';
import IconButton from '@material-ui/core/IconButton';
import InputLabel from '@material-ui/core/InputLabel';
import MenuItem from '@material-ui/core/MenuItem';
import Radio from '@material-ui/core/Radio';
import RadioGroup from '@material-ui/core/RadioGroup';
import Select from '@material-ui/core/Select';
import Switch from '@material-ui/core/Switch';
import TextField from '@material-ui/core/TextField';
import Tooltip from '@material-ui/core/Tooltip';
import Typography from '@material-ui/core/Typography';
import { makeStyles } from '@material-ui/core/styles';
import AddIcon from '@material-ui/icons/Add';
import DeleteOutlineIcon from '@material-ui/icons/DeleteOutline';
import PersonIcon from '@material-ui/icons/Person';
import {
  ALLOWED_ENVIRONMENTS,
  type Environment,
} from '@internal/backstage-plugin-vault-common';

// ─── Domain types ────────────────────────────────────────────────────────────

// The kind of application being onboarded. Determines the capability defaults
// and ceiling enforced by OPA. Sourced from allowed_capabilities_by_type.json.
export type ApplicationType =
  | 'standard-service'
  | 'ldap-integrated'
  | 'database-backed'
  | 'kubernetes-workload'
  | 'vm-infrastructure'
  | 'pki-consumer' // SE-exclusive — gated by isSeEligible prop
  | 'ssc';

// machine: service identity only (cert auth + machine KV policy)
// both: machine + human access (adds LDAP groups + human KV policies via Workflow A)
export type AccessType = 'machine' | 'both';

// Additive, multi-select — "My application is deployed on" (agreed July 31,
// 2026, code-review/showcase). Deliberately additive alongside
// ApplicationType rather than replacing it: migrating the KV
// capability-ceiling model to be platform-conditional (per the same
// discussion, GH #605) is a separate, not-yet-ICE-approved follow-up and is
// not attempted here. See docs/client/07-application-onboarding-form.md.
export type DeploymentTarget = 'vm-onprem' | 'k8s-onprem' | 'k8s-cloud';

// KV capabilities available through the IDP form.
// "delete" is never available — requires a separate change request.
export type Capability = 'read' | 'list' | 'create' | 'update';

// Sourced from @internal/backstage-plugin-vault-common's ALLOWED_ENVIRONMENTS
// (GH #1013) — previously a hand-copied literal here with no shared source.
export type { Environment };

// A single delegated-access grant: gives an LDAP/AD group scoped access to a
// sub-path beneath the BSN's KV space. Maps to one entry of the manifest's
// kv_access.delegated_access list. Optional and repeatable.
export interface DelegatedAccessGrant {
  subpath: string; // relative sub-path under the BSN KV path, e.g. "db"
  group: string; // LDAP/AD group, e.g. "grp-dbplatform-readonly"
  capabilities: Capability[]; // subset of read/list/create/update
}

export type SharedSecretAccessLevel = 'ro' | 'rw';

// A single shared-secret consumer opt-in: this BSN's estate consuming a
// secret owned by another BSN (Workflow A opt-in, GH #991/#990). Maps to one
// entry of the manifest's shared_consumer list. Optional and repeatable —
// one-to-few model, one entry per opted-in secret.
export interface SharedSecretConsumerGrant {
  ownerBsn: string; // BSN that owns the shared secret, e.g. "BSN-1234"
  secretName: string; // name of the shared secret / path segment, e.g. "apikey"
  accessLevel: SharedSecretAccessLevel; // ro (default) | rw (coordinated approval)
}

// ─── Props interfaces ─────────────────────────────────────────────────────────

// BSN options sourced from the ServiceNow BSN Catalog plugin
// (GET /api/snow-bsn-catalog/bsns). Passed in by the parent.
export interface BsnOption {
  id: string; // e.g. "BSN-1234"
  label: string; // e.g. "BSN-1234 — Trading Platform Core"
  inProgress?: boolean; // true when a Vault estate request is already in flight for this BSN
}

// User options sourced from the identity provider (LDAP/AD).
export interface OwnerOption {
  id: string;
  label: string;
  type: 'user' | 'team';
  // True when the user is in the global SE eligibility group in ForgeRock.
  // When any SE-eligible user is in the users list, the SE path capabilities
  // section is shown automatically in the form.
  seEligible?: boolean;
}

// Submitted form values. techLead, managers, approvers are display-only
// and are NOT included — they are resolved by SNOW natively at routing time.
export interface VaultOnboardingFormValues {
  bsns: string[]; // one or more BSNs — same Vault config applied to each
  applicationType?: ApplicationType | ''; // kept for backward compat; no longer shown in UI
  deploymentTargets: DeploymentTarget[];
  requestType?: 'new' | 'edit' | ''; // kept for backward compat; no longer shown in UI
  accessType: AccessType | '';
  capabilities: Capability[];

  environment: Environment[];
  users: OwnerOption[];
  // The person designated as this BSN's group owner in ForgeRock. Candidates
  // are the tech lead plus whoever is in `users` — see forgeRockOwnerCandidates.
  forgeRockOwner: OwnerOption | null;
  justification: string;
  delegatedAccess: DelegatedAccessGrant[];
  sharedSecretConsumers: SharedSecretConsumerGrant[];
  targetClusters: string[];
  additionalEngines: OptInEngine[];
  enginesByCluster: Record<string, OptInEngine[]>;
  pki: PkiConfig | null;
  database: DatabaseConfig | null;
  awsIam: AwsIamConfig | null;
  sshCa: SshCaConfig | null;
  // When true (default, matches pre-toggle behavior) the single config above
  // applies to every datacenter that has the engine checked. When false, each
  // datacenter gets its own config from the *ByCluster map below.
  engineApplyToAll: Record<OptInEngine, boolean>;
  pkiByCluster: Record<string, PkiConfig | null>;
  databaseByCluster: Record<string, DatabaseConfig | null>;
  awsIamByCluster: Record<string, AwsIamConfig | null>;
  sshCaByCluster: Record<string, SshCaConfig | null>;
}

export interface VaultOnboardingFormProps {
  bsnOptions?: BsnOption[];
  isLoadingBsns?: boolean;

  // User search options for the Users multi-select (from LDAP/AD).
  userOptions?: OwnerOption[];
  isLoadingUsers?: boolean;

  // Auto-populated from the BSN cache when a BSN is selected.
  // techLead: CMDB technical_lead field (ServiceNow).
  // approvers: CMDB technical_approval_groups (ServiceNow) — Stage 2 approvers.
  techLead?: string;
  approvers?: string[];
  // True while the parent is resolving the tech lead for the selected BSN.
  // When false and techLead is empty, the form shows "no tech lead on record"
  // (a BSN legitimately may not have one) rather than a perpetual "Loading…".
  isLoadingTechLead?: boolean;

  // Derived from ForgeRock manager lookup per selected user.
  // Deduplicated union of all managers — updated whenever users list changes.
  // Parent is responsible for fetching; component fires onUsersChange to trigger it.
  managers?: string[];
  isLoadingManagers?: boolean;

  // Called whenever the users list changes so the parent can trigger the
  // ForgeRock manager lookup and pass results back via the managers prop.
  onUsersChange?: (users: OwnerOption[]) => void;

  // Called whenever the BSN selection changes so the parent can fetch
  // techLead and approvers from the BSN cache and pass them back as props.
  onBsnChange?: (bsn: string) => void;

  // Resolved from the current user's LDAP memberOf at form load.
  // When false, pki-consumer application type is disabled.
  isSeEligible?: boolean;

  // Pre-populate the form fields (used by stories to render a filled state).
  initialValues?: Partial<VaultOnboardingFormValues>;

  onSubmit?: (values: VaultOnboardingFormValues) => void;
  isSubmitting?: boolean;
  // Called on every state change when used as a scaffolder field extension.
  // When provided, the submit button is hidden — the scaffolder's "Next" drives progression.
  onValuesChange?: (values: VaultOnboardingFormValues) => void;
  // OPA is a hard dependency of Gate 1 (validateWithOpa.ts already fails
  // closed on unreachable) — this just surfaces that before the user wastes
  // time filling out a request that's doomed at submit. Polling for
  // reachability is the caller's responsibility (see
  // VaultOnboardingFormField.tsx); this component only renders the result.
  opaDown?: boolean;
}

const DEPLOYMENT_TARGETS: { value: DeploymentTarget; label: string }[] = [
  { value: 'vm-onprem', label: 'VM / physical — on-prem' },
  { value: 'k8s-onprem', label: 'Kubernetes / OCP — on-prem' },
  { value: 'k8s-cloud', label: 'Kubernetes / OCP — cloud' },
];

const ALL_CAPABILITIES: { value: Capability; label: string }[] = [
  { value: 'read', label: 'Read' },
  { value: 'list', label: 'List' },
  { value: 'create', label: 'Create' },
  { value: 'update', label: 'Update' },
];

// ─── Opt-in engine types ──────────────────────────────────────────────────────

export type OptInEngine = 'pki' | 'database' | 'aws_iam' | 'ssh_ca';

export type PkiKeyUsage =
  | 'DigitalSignature'
  | 'ContentCommitment'
  | 'KeyEncipherment'
  | 'DataEncipherment'
  | 'KeyAgreement'
  | 'CertSign'
  | 'CRLSign'
  | 'EncipherOnly'
  | 'DecipherOnly';

export interface PkiConfig {
  allowedDomains: string; // raw textarea, newline-separated DNS patterns
  needsCertSigning: boolean; // true when app signs (issues) certs, not just verifies
  advancedSettingsEnabled: boolean;
  // Only meaningful when advancedSettingsEnabled is true and the derived
  // role isn't already at the platform ceiling (kubernetes_365d).
  customMaxTtl: string; // duration string, e.g. "300d"
  // readonly: test fixtures set this via an `as const` tuple literal
  // (matching the existing SSH CA test convention in this file), which
  // TypeScript infers as a readonly array — widened here so both mutable
  // and readonly array literals are assignable.
  keyUsage: readonly PkiKeyUsage[];
  extKeyUsage: string; // comma-separated, e.g. "ServerAuth, ClientAuth"
  // Required when Advanced Settings changes any value away from default.
  justification: string;
}

export function derivePkiRoleName(
  deploymentTargets: DeploymentTarget[],
): string {
  const isK8s =
    deploymentTargets.includes('k8s-onprem') ||
    deploymentTargets.includes('k8s-cloud');
  return isK8s ? 'kubernetes_365d' : 'app_agent_200d';
}

export function pkiDefaultMaxTtl(roleName: string): string {
  return roleName === 'kubernetes_365d' ? '365d' : '200d';
}

export const PKI_KEY_USAGE_OPTIONS: PkiKeyUsage[] = [
  'DigitalSignature',
  'ContentCommitment',
  'KeyEncipherment',
  'DataEncipherment',
  'KeyAgreement',
  'CertSign',
  'CRLSign',
  'EncipherOnly',
  'DecipherOnly',
];

export const DEFAULT_PKI_KEY_USAGE: PkiKeyUsage[] = [
  'DigitalSignature',
  'KeyEncipherment',
];

export const DEFAULT_PKI_EXT_KEY_USAGE = 'ServerAuth, ClientAuth';

export interface DatabaseConfig {
  backend: string; // selected from allowed_backends list
  rotationTtl: string; // duration string, e.g. "24h"
}

export interface AwsIamConfig {
  iamRole: string; // selected from iam_roles list
  credentialTtl: string; // duration string, e.g. "1h"
}

export type SshCertType = 'user' | 'host';

export interface SshCaConfig {
  // Path A only (ships in MVP): the requester picks a role Vault already has
  // configured — no new role is created. Path B (create a new role) is
  // deferred pending the second-approval-group confirmation (SSH-ROLE-APPR).
  roleName: string;
  certType: SshCertType | '';
  maxTtl: string; // duration string, validated against the platform ceiling (e.g. "96h")
  defaultTtl: string; // duration string, used when the signing request omits a TTL (e.g. "8h")
  allowedUsers: string; // newline-separated; required when certType is 'user'
  defaultUser: string; // singular; required when certType is 'user'; must be one of allowedUsers
  allowedHosts: string; // newline-separated; required when certType is 'host'
  allowedExtensions: string; // newline-separated, e.g. "permit-pty"
  justification?: string; // optional free-text context for the approver
}

// Fresh config seeded when an engine is first checked (globally, or for a
// single datacenter in per-datacenter mode) — one factory per engine so the
// literal isn't duplicated across handleEngineToggle and the per-cluster
// seeding in handleEngineApplyToAllToggle.
function defaultPkiConfig(): PkiConfig {
  return {
    allowedDomains: '',
    needsCertSigning: false,
    advancedSettingsEnabled: false,
    customMaxTtl: '',
    keyUsage: [...DEFAULT_PKI_KEY_USAGE],
    extKeyUsage: DEFAULT_PKI_EXT_KEY_USAGE,
    justification: '',
  };
}

function defaultDatabaseConfig(): DatabaseConfig {
  return { backend: '', rotationTtl: '24h' };
}

function defaultAwsIamConfig(): AwsIamConfig {
  return { iamRole: '', credentialTtl: '1h' };
}

function defaultSshCaConfig(): SshCaConfig {
  return {
    roleName: '',
    certType: '' as const,
    maxTtl: '',
    defaultTtl: '',
    allowedUsers: '',
    defaultUser: '',
    allowedHosts: '',
    allowedExtensions: '',
    justification: '',
  };
}

const OPT_IN_ENGINES: { value: OptInEngine; label: string }[] = [
  { value: 'pki', label: 'PKI' },
  { value: 'database', label: 'Database' },
  { value: 'aws_iam', label: 'AWS IAM' },
  { value: 'ssh_ca', label: 'SSH CA' },
];

// Fixture lists mirroring allowed_backends.json in the OPA bundle.
// Production: fetch from GET /api/vault/allowed-backends.
export const DATABASE_BACKENDS = [
  'oracle-nonprod',
  'oracle-prod',
  'postgres-nonprod',
  'postgres-prod',
  'oracle-dev',
  'postgres-dev',
];

const IAM_ROLES = [
  'arn:aws:iam::123456789012:role/bsn-nonprod-app-role',
  'arn:aws:iam::123456789012:role/bsn-prod-app-role',
  'arn:aws:iam::123456789012:role/bsn-dev-app-role',
  'arn:aws:iam::000000000000:role/bsn-9999-test-role', // LocalStack dev stack
];

// Exported for reuse by VaultEstateProfile's engine edit dialogs — the same
// duration format applies to a TTL field whether it's being set at onboarding
// or edited afterward, and duplicating the regex risks the two drifting.
export const DURATION_PATTERN = /^[0-9]+(d|h|m|s)$/;

// Exported for the same reason as DURATION_PATTERN above.
export const PKI_DOMAIN_PATTERN = /^(\*\.)?([a-zA-Z0-9-]+\.)+[a-zA-Z]{2,}$/;

// Converts a duration string already known to match DURATION_PATTERN into
// whole/fractional days, for comparing custom PKI durations against the
// 200d/365d bounds. Returns NaN for anything that doesn't match — callers
// must check DURATION_PATTERN first (existing convention in this file).
function durationToDays(value: string): number {
  const match = value.match(/^([0-9]+)(d|h|m|s)$/);
  if (!match) return NaN;
  const amount = Number(match[1]);
  const secondsPerUnit: Record<string, number> = {
    d: 86400,
    h: 3600,
    m: 60,
    s: 1,
  };
  return (amount * secondsPerUnit[match[2]]) / 86400;
}

// ICE's service-account naming guardrail (design doc: "svc naming-convention
// guardrail" — allowed-user entries must carry the svc- prefix; non-conforming
// accounts are refused by the form). Format-only — this does not verify the
// account actually exists (that check is blocked on Seth providing Git repo
// access to the real inventory; see SSH-ROLE-APPR).
const SVC_PREFIX_PATTERN = /^svc-/;

// Per-field validation errors, keyed by engine-prefixed field name so a single
// flat map can hold errors for multiple engines/config boxes without collision.
export type FieldErrors = Partial<
  Record<
    | keyof VaultOnboardingFormValues
    | 'pkiDomains'
    | 'pkiMaxTtl'
    | 'pkiKeyUsage'
    | 'pkiExtKeyUsage'
    | 'pkiJustification'
    | 'dbBackend'
    | 'dbRotationTtl'
    | 'awsIamRole'
    | 'awsCredentialTtl'
    | 'sshCaRoleName'
    | 'sshCaCertType'
    | 'sshCaMaxTtl'
    | 'sshCaDefaultTtl'
    | 'sshCaAllowedUsers'
    | 'sshCaDefaultUser'
    | 'sshCaAllowedHosts'
    | 'sshCaAllowedExtensions'
    | 'sshCaJustification',
    string
  >
>;

// Pure per-engine validators — used both for the shared config (once) and for
// each per-datacenter config (once per datacenter) when apply-to-all is off.
function validatePkiConfig(pki: PkiConfig, pkiRoleName: string): FieldErrors {
  const errors: FieldErrors = {};
  const domains = pki.allowedDomains
    .split('\n')
    .map(d => d.trim())
    .filter(Boolean);
  if (domains.length === 0) {
    errors.pkiDomains = 'At least one allowed domain is required for PKI';
  } else {
    const invalid = domains.find(d => !PKI_DOMAIN_PATTERN.test(d));
    if (invalid) errors.pkiDomains = `Invalid domain pattern: '${invalid}'`;
  }

  if (pki.advancedSettingsEnabled) {
    if (pkiRoleName !== 'kubernetes_365d' && pki.customMaxTtl.trim() !== '') {
      if (!DURATION_PATTERN.test(pki.customMaxTtl)) {
        errors.pkiMaxTtl =
          'Duration must match format: Xd, Xh, Xm, or Xs (e.g. 300d)';
      } else {
        const days = durationToDays(pki.customMaxTtl);
        if (days <= 200) {
          errors.pkiMaxTtl =
            'Custom duration must be greater than the 200-day default';
        } else if (days > 365) {
          errors.pkiMaxTtl =
            'Custom duration cannot exceed the platform ceiling of 365 days';
        }
      }
    }

    const pkiChangedFromDefault =
      (pkiRoleName !== 'kubernetes_365d' &&
        pki.customMaxTtl.trim() !== '' &&
        DURATION_PATTERN.test(pki.customMaxTtl) &&
        durationToDays(pki.customMaxTtl) > 200 &&
        durationToDays(pki.customMaxTtl) <= 365) ||
      pki.keyUsage.length !== DEFAULT_PKI_KEY_USAGE.length ||
      !DEFAULT_PKI_KEY_USAGE.every(u => pki.keyUsage.includes(u)) ||
      pki.extKeyUsage.trim() !== DEFAULT_PKI_EXT_KEY_USAGE;

    if (pki.keyUsage.length === 0) {
      errors.pkiKeyUsage = 'Select at least one Key Usage value';
    }
    if (!pki.extKeyUsage.trim()) {
      errors.pkiExtKeyUsage = 'Extended Key Usage is required';
    }
    if (pkiChangedFromDefault && !pki.justification.trim()) {
      errors.pkiJustification =
        'Business justification is required when Advanced Settings changes a default';
    }
  }
  return errors;
}

function validateDatabaseConfig(database: DatabaseConfig): FieldErrors {
  const errors: FieldErrors = {};
  if (!database.backend) {
    errors.dbBackend = 'Database backend is required';
  }
  if (!DURATION_PATTERN.test(database.rotationTtl)) {
    errors.dbRotationTtl =
      'Duration must match format: Xd, Xh, Xm, or Xs (e.g. 24h, 7d)';
  }
  return errors;
}

function validateAwsIamConfig(awsIam: AwsIamConfig): FieldErrors {
  const errors: FieldErrors = {};
  if (!awsIam.iamRole) {
    errors.awsIamRole = 'IAM role is required';
  }
  if (!DURATION_PATTERN.test(awsIam.credentialTtl)) {
    errors.awsCredentialTtl =
      'Duration must match format: Xd, Xh, Xm, or Xs (e.g. 1h)';
  }
  return errors;
}

function validateSshCaConfig(sshCa: SshCaConfig): FieldErrors {
  const errors: FieldErrors = {};
  if (!sshCa.roleName.trim()) {
    errors.sshCaRoleName = 'An existing SSH CA role is required';
  }
  if (!sshCa.certType) {
    errors.sshCaCertType = 'Certificate type is required';
  }
  if (!DURATION_PATTERN.test(sshCa.maxTtl)) {
    errors.sshCaMaxTtl =
      'Duration must match format: Xd, Xh, Xm, or Xs (e.g. 96h)';
  }
  if (!DURATION_PATTERN.test(sshCa.defaultTtl)) {
    errors.sshCaDefaultTtl =
      'Duration must match format: Xd, Xh, Xm, or Xs (e.g. 8h)';
  }
  if (sshCa.certType === 'user' && !sshCa.allowedUsers.trim()) {
    errors.sshCaAllowedUsers = 'At least one allowed user is required';
  } else if (sshCa.certType === 'user') {
    const invalidUser = sshCa.allowedUsers
      .split('\n')
      .map(u => u.trim())
      .filter(Boolean)
      .find(u => !SVC_PREFIX_PATTERN.test(u));
    if (invalidUser) {
      errors.sshCaAllowedUsers = `'${invalidUser}' must use the svc- service-account prefix`;
    }
  }
  if (sshCa.certType === 'user' && !sshCa.defaultUser.trim()) {
    errors.sshCaDefaultUser = 'Default user is required';
  } else if (
    sshCa.certType === 'user' &&
    !SVC_PREFIX_PATTERN.test(sshCa.defaultUser.trim())
  ) {
    errors.sshCaDefaultUser = `'${sshCa.defaultUser.trim()}' must use the svc- service-account prefix`;
  }
  if (sshCa.certType === 'host' && !sshCa.allowedHosts.trim()) {
    errors.sshCaAllowedHosts = 'At least one allowed host is required';
  }
  if (!sshCa.allowedExtensions.trim()) {
    errors.sshCaAllowedExtensions = 'At least one extension is required';
  }
  if (!sshCa.justification?.trim()) {
    errors.sshCaJustification = 'Justification is required for SSH CA access';
  }
  return errors;
}

// Record<Environment, string> is exhaustive — TS forces this to be updated
// if ALLOWED_ENVIRONMENTS ever gains a value, the same safety net
// STATE_COLORS already relies on for RequestState (see
// EntityVaultRequestOverviewCard.tsx).
const ENVIRONMENT_LABELS: Record<Environment, string> = {
  production: 'Production',
  'non-production': 'Non-Production',
  development: 'Development',
};

export const ENVIRONMENTS: { value: Environment; label: string }[] =
  ALLOWED_ENVIRONMENTS.map(value => ({
    value,
    label: ENVIRONMENT_LABELS[value],
  }));

// SE-specific path capabilities — automatically granted when any SE-eligible user
// is added to the users list. Always read-only; not configurable by the requester.
// Path names and capabilities defined in se_capabilities.json (OPA bundle, open item C-23).
// Production: fetched from GET /api/vault/se-capabilities (Backend Plugin serves OPA bundle cache).
const SE_PATH_CAPABILITIES = [
  { path: 'server/', label: 'Root User Keys', capability: 'read' },
  { path: 'route-passwords/', label: 'Password Keys', capability: 'read' },
] as const;

// Lab / RPT Backstage: only the two in-cluster Vault demos.
// Production ICE: DatacenterPicker fetches from GET /api/vault/datacenters.
// Exported so VaultEstateProfile.tsx's Change Datacenters edit dialog uses
// the same cluster list/constraints instead of free-text entry.
export const DATACENTERS = [
  { id: 'vault-demo-a', label: 'Vault Demo A' },
  { id: 'vault-demo-b', label: 'Vault Demo B' },
];

const DEFAULT_CLUSTERS = ['vault-demo-a', 'vault-demo-b'];
export const MIN_CLUSTERS = 1;
export const MAX_CLUSTERS = 2;

// Mock engine availability per cluster. Production: sourced from GET /api/vault/cluster-capabilities.
// An engine is offered only when ALL selected clusters support it.
const CLUSTER_ENGINE_AVAILABILITY: Record<string, OptInEngine[]> = {
  'vault-demo-a': ['pki', 'database', 'aws_iam', 'ssh_ca'],
  'vault-demo-b': ['pki', 'database', 'ssh_ca'],
};

// ─── Styles ───────────────────────────────────────────────────────────────────

const useStyles = makeStyles(theme => ({
  root: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(3),
    maxWidth: 560,
    padding: theme.spacing(3),
  },
  card: {
    padding: theme.spacing(3),
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(2.5),
  },
  cardTitle: {
    fontWeight: 600,
    marginBottom: theme.spacing(0.5),
  },
  chips: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: theme.spacing(0.5),
  },
  capabilityRow: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
  },
  capabilityChip: {
    height: 18,
    fontSize: '0.65rem',
  },
  ownerOption: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
    width: '100%',
  },
  ownerOptionLabel: {
    flex: 1,
  },
  // Read-only display fields (techLead, managers, approvers)
  readOnlyBlock: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(0.5),
  },
  readOnlyLabel: {
    fontSize: '0.75rem',
    color: theme.palette.text.secondary,
    fontWeight: 500,
  },
  readOnlyValue: {
    padding: theme.spacing(1.25, 1.75),
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: theme.shape.borderRadius,
    backgroundColor: theme.palette.action.hover,
    minHeight: 40,
    display: 'flex',
    alignItems: 'center',
  },
  chipList: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: theme.spacing(0.5),
    padding: theme.spacing(1, 1.75),
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: theme.shape.borderRadius,
    backgroundColor: theme.palette.action.hover,
    minHeight: 40,
  },
  warningText: {
    color: theme.palette.warning.dark,
    fontSize: '0.8rem',
  },
  disabledOptionWrapper: {
    display: 'block',
    width: '100%',
  },
  delegatedSection: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(1.5),
    alignItems: 'stretch',
  },
  delegatedGrant: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(1.5),
    padding: theme.spacing(2),
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: theme.shape.borderRadius,
  },
  delegatedGrantHeader: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  delegatedGrantTitle: {
    fontWeight: 500,
    fontSize: '0.85rem',
    color: theme.palette.text.secondary,
  },
  delegatedList: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(1.5),
  },
  addButton: {
    alignSelf: 'flex-start',
    textTransform: 'none',
  },
}));

// ─── Engine config field groups ────────────────────────────────────────────────
//
// Each of these renders one engine's config fields against a plain `config`
// object and reports changes via `onChange(patch)` — no knowledge of whether
// it's the shared config or one datacenter's config out of a *ByCluster map.
// The caller (VaultOnboardingForm) decides which and wraps it in a <Paper>
// titled either "X Configuration" (shared) or "X Configuration — DC" (one per
// datacenter, apply-to-all off). `idPrefix` keeps DOM ids unique when several
// instances of the same fields render at once.

function PkiConfigFields({
  idPrefix,
  config,
  onChange,
  errors,
  pkiRoleName,
  pkiDefaultTtl,
  classes,
}: {
  idPrefix: string;
  config: PkiConfig;
  onChange: (patch: Partial<PkiConfig>) => void;
  errors: FieldErrors;
  pkiRoleName: string;
  pkiDefaultTtl: string;
  classes: Record<string, string>;
}) {
  const hasIceDomainWarning = config.allowedDomains
    .split('\n')
    .map(d => d.trim())
    .filter(Boolean)
    .some(d => {
      const lower = d.toLowerCase();
      return lower === 'ice.com' || lower.endsWith('.ice.com');
    });

  return (
    <>
      <Typography variant="body2" color="textSecondary">
        Role: <strong>{pkiRoleName}</strong> — grants sign/issue for up to{' '}
        {config.customMaxTtl.trim() || pkiDefaultTtl}. Key Usage:{' '}
        {config.keyUsage.join(', ')}. Extended Key Usage:{' '}
        {config.extKeyUsage || DEFAULT_PKI_EXT_KEY_USAGE}.
      </Typography>

      <FormControlLabel
        control={
          <Checkbox
            checked={config.needsCertSigning}
            onChange={e => onChange({ needsCertSigning: e.target.checked })}
            color="primary"
          />
        }
        label="Will this application sign certificates (not just verify them)?"
      />

      <TextField
        id={`${idPrefix}-allowed-domains`}
        label="Allowed Domains"
        placeholder={'*.bsn1234.ice.com\n*.internal.ice.com'}
        fullWidth
        multiline
        rows={3}
        variant="outlined"
        value={config.allowedDomains}
        onChange={e => onChange({ allowedDomains: e.target.value })}
        error={!!errors.pkiDomains}
        helperText={
          errors.pkiDomains ??
          'DNS name patterns this BSN may request certificates for, one per line (e.g. *.bsn1234.ice.com)'
        }
      />

      {hasIceDomainWarning && (
        <Typography variant="body2" className={classes.warningText}>
          This engine issues internal-only certificates. For a domain requiring
          public trust, use the external certificate process instead (link TBD).
        </Typography>
      )}

      <FormControlLabel
        control={
          <Checkbox
            checked={config.advancedSettingsEnabled}
            onChange={() =>
              onChange({
                advancedSettingsEnabled: !config.advancedSettingsEnabled,
              })
            }
            color="primary"
          />
        }
        label="Advanced Settings"
      />

      {config.advancedSettingsEnabled && (
        <>
          <TextField
            id={`${idPrefix}-custom-max-ttl`}
            label="Custom Duration"
            placeholder="300d"
            fullWidth
            variant="outlined"
            disabled={pkiRoleName === 'kubernetes_365d'}
            value={config.customMaxTtl}
            onChange={e => onChange({ customMaxTtl: e.target.value })}
            error={!!errors.pkiMaxTtl}
            helperText={
              errors.pkiMaxTtl ??
              (pkiRoleName === 'kubernetes_365d'
                ? 'Already at the platform maximum (365d) — raising it requires the Vault Platform Team approval gate (follow-up ticket)'
                : 'Must be greater than 200d and no more than the platform ceiling of 365d')
            }
          />

          <FormControl component="fieldset" error={!!errors.pkiKeyUsage}>
            <FormLabel component="legend">Key Usage</FormLabel>
            <FormGroup row>
              {PKI_KEY_USAGE_OPTIONS.map(usage => (
                <FormControlLabel
                  key={usage}
                  control={
                    <Checkbox
                      checked={config.keyUsage.includes(usage)}
                      onChange={() => {
                        const has = config.keyUsage.includes(usage);
                        const keyUsage = has
                          ? config.keyUsage.filter(u => u !== usage)
                          : [...config.keyUsage, usage];
                        onChange({ keyUsage });
                      }}
                      color="primary"
                    />
                  }
                  label={usage}
                />
              ))}
            </FormGroup>
            <FormHelperText>
              {errors.pkiKeyUsage ??
                'Overrides the standard DigitalSignature + KeyEncipherment default'}
            </FormHelperText>
          </FormControl>

          <TextField
            id={`${idPrefix}-ext-key-usage`}
            label="Extended Key Usage"
            placeholder="ServerAuth, ClientAuth"
            fullWidth
            variant="outlined"
            value={config.extKeyUsage}
            onChange={e => onChange({ extKeyUsage: e.target.value })}
            error={!!errors.pkiExtKeyUsage}
            helperText={
              errors.pkiExtKeyUsage ??
              'Comma-separated. Overrides the standard ServerAuth, ClientAuth default.'
            }
          />

          <TextField
            id={`${idPrefix}-justification`}
            label="Business Justification"
            placeholder="Why does this application need a non-standard PKI role?"
            fullWidth
            multiline
            rows={3}
            variant="outlined"
            value={config.justification}
            onChange={e => onChange({ justification: e.target.value })}
            error={!!errors.pkiJustification}
            helperText={
              errors.pkiJustification ??
              'Required when Advanced Settings changes any default value'
            }
          />
        </>
      )}
    </>
  );
}

function DatabaseConfigFields({
  idPrefix,
  config,
  onChange,
  errors,
}: {
  idPrefix: string;
  config: DatabaseConfig;
  onChange: (patch: Partial<DatabaseConfig>) => void;
  errors: FieldErrors;
}) {
  return (
    <>
      <Typography variant="body2" color="textSecondary">
        Vault rotates a pre-existing database user's password on a schedule
        (static role). The application reads the current credential from Vault
        on each connection or rotation event.
      </Typography>

      <FormControl variant="outlined" fullWidth error={!!errors.dbBackend}>
        <InputLabel id={`${idPrefix}-backend-label`}>
          Database Backend
        </InputLabel>
        <Select
          labelId={`${idPrefix}-backend-label`}
          label="Database Backend"
          value={config.backend}
          onChange={e => onChange({ backend: e.target.value as string })}
        >
          {DATABASE_BACKENDS.map(b => (
            <MenuItem key={b} value={b}>
              {b}
            </MenuItem>
          ))}
        </Select>
        <FormHelperText>
          {errors.dbBackend ??
            'Pre-configured database backend. Contact the platform team to add new backends.'}
        </FormHelperText>
      </FormControl>

      <TextField
        id={`${idPrefix}-rotation-ttl`}
        label="Rotation TTL"
        placeholder="24h"
        fullWidth
        variant="outlined"
        value={config.rotationTtl}
        onChange={e => onChange({ rotationTtl: e.target.value })}
        error={!!errors.dbRotationTtl}
        helperText={
          errors.dbRotationTtl ??
          'How often Vault rotates the credential (e.g. 24h, 7d). Platform ceiling enforced by OPA.'
        }
      />
    </>
  );
}

function AwsIamConfigFields({
  idPrefix,
  config,
  onChange,
  errors,
}: {
  idPrefix: string;
  config: AwsIamConfig;
  onChange: (patch: Partial<AwsIamConfig>) => void;
  errors: FieldErrors;
}) {
  return (
    <>
      <Typography variant="body2" color="textSecondary">
        Vault generates temporary AWS credentials (STS) scoped to the selected
        IAM role. The IAM role defines AWS permissions — pre-configured by the
        platform team per environment.
      </Typography>

      <FormControl variant="outlined" fullWidth error={!!errors.awsIamRole}>
        <InputLabel id={`${idPrefix}-role-label`}>IAM Role</InputLabel>
        <Select
          labelId={`${idPrefix}-role-label`}
          label="IAM Role"
          value={config.iamRole}
          onChange={e => onChange({ iamRole: e.target.value as string })}
        >
          {IAM_ROLES.map(r => (
            <MenuItem key={r} value={r}>
              {r}
            </MenuItem>
          ))}
        </Select>
        <FormHelperText>
          {errors.awsIamRole ??
            'Pre-configured IAM role for this environment. Contact the platform team to add new roles.'}
        </FormHelperText>
      </FormControl>

      <TextField
        id={`${idPrefix}-credential-ttl`}
        label="Credential TTL"
        placeholder="1h"
        fullWidth
        variant="outlined"
        value={config.credentialTtl}
        onChange={e => onChange({ credentialTtl: e.target.value })}
        error={!!errors.awsCredentialTtl}
        helperText={
          errors.awsCredentialTtl ??
          'How long the AWS credentials remain valid (e.g. 1h). Platform ceiling enforced by OPA.'
        }
      />
    </>
  );
}

function SshCaConfigFields({
  idPrefix,
  config,
  onChange,
  errors,
}: {
  idPrefix: string;
  config: SshCaConfig;
  onChange: (patch: Partial<SshCaConfig>) => void;
  errors: FieldErrors;
}) {
  return (
    <>
      <TextField
        id={`${idPrefix}-role-name`}
        label="Role Name"
        placeholder="tenable_ssh"
        fullWidth
        variant="outlined"
        value={config.roleName}
        onChange={e => onChange({ roleName: e.target.value })}
        error={!!errors.sshCaRoleName}
        helperText={
          errors.sshCaRoleName ??
          'An existing SSH CA role Vault already has configured. Requesting a new role is not yet supported — contact the Vault team.'
        }
      />

      <FormControl component="fieldset" error={!!errors.sshCaCertType}>
        <FormLabel component="legend">Certificate Type</FormLabel>
        <RadioGroup
          aria-label="certificate type"
          name={`${idPrefix}-cert-type`}
          value={config.certType}
          onChange={e => onChange({ certType: e.target.value as SshCertType })}
          row
        >
          <FormControlLabel
            value="user"
            label="User"
            control={<Radio color="primary" />}
          />
          <FormControlLabel
            value="host"
            label="Host"
            control={<Radio color="primary" />}
          />
        </RadioGroup>
        <FormHelperText>
          {errors.sshCaCertType ??
            'User: signs certs authorizing login as a principal. Host: signs the target host’s own identity.'}
        </FormHelperText>
      </FormControl>

      {config.certType === 'user' && (
        <TextField
          id={`${idPrefix}-allowed-users`}
          label="Allowed Users"
          placeholder={'svc-app-nonprod'}
          fullWidth
          multiline
          minRows={2}
          variant="outlined"
          value={config.allowedUsers}
          onChange={e => onChange({ allowedUsers: e.target.value })}
          error={!!errors.sshCaAllowedUsers}
          helperText={
            errors.sshCaAllowedUsers ??
            'One principal per line, each carrying the svc- prefix. Signed certs authorize login as these users only.'
          }
        />
      )}

      {config.certType === 'user' && (
        <TextField
          id={`${idPrefix}-default-user`}
          label="Default User"
          placeholder="svc-app-nonprod"
          fullWidth
          variant="outlined"
          value={config.defaultUser}
          onChange={e => onChange({ defaultUser: e.target.value })}
          error={!!errors.sshCaDefaultUser}
          helperText={
            errors.sshCaDefaultUser ??
            'Used when the signing request omits a principal. Must be one of the allowed users above.'
          }
        />
      )}

      {config.certType === 'host' && (
        <TextField
          id={`${idPrefix}-allowed-hosts`}
          label="Allowed Hosts"
          placeholder={'app.ice.com'}
          fullWidth
          multiline
          minRows={2}
          variant="outlined"
          value={config.allowedHosts}
          onChange={e => onChange({ allowedHosts: e.target.value })}
          error={!!errors.sshCaAllowedHosts}
          helperText={
            errors.sshCaAllowedHosts ??
            'One hostname per line. Signed certs authorize these hosts to prove their identity.'
          }
        />
      )}

      <TextField
        id={`${idPrefix}-max-ttl`}
        label="Maximum TTL"
        placeholder="96h"
        fullWidth
        variant="outlined"
        value={config.maxTtl}
        onChange={e => onChange({ maxTtl: e.target.value })}
        error={!!errors.sshCaMaxTtl}
        helperText={
          errors.sshCaMaxTtl ??
          'Ceiling on certificate lifetime (e.g. 96h). Platform ceiling enforced by OPA at submission.'
        }
      />

      <TextField
        id={`${idPrefix}-default-ttl`}
        label="Default TTL"
        placeholder="8h"
        fullWidth
        variant="outlined"
        value={config.defaultTtl}
        onChange={e => onChange({ defaultTtl: e.target.value })}
        error={!!errors.sshCaDefaultTtl}
        helperText={
          errors.sshCaDefaultTtl ??
          'Used when the signing request omits a TTL (e.g. 8h). Must not exceed Maximum TTL.'
        }
      />

      <TextField
        id={`${idPrefix}-allowed-extensions`}
        label="Allowed Extensions"
        placeholder="permit-pty"
        fullWidth
        multiline
        minRows={2}
        variant="outlined"
        value={config.allowedExtensions}
        onChange={e => onChange({ allowedExtensions: e.target.value })}
        error={!!errors.sshCaAllowedExtensions}
        helperText={
          errors.sshCaAllowedExtensions ??
          'One extension per line (e.g. permit-pty).'
        }
      />

      <TextField
        id={`${idPrefix}-justification`}
        label="Additional Justification"
        placeholder="Why does this application need SSH CA access?"
        fullWidth
        multiline
        rows={3}
        variant="outlined"
        value={config.justification ?? ''}
        onChange={e => onChange({ justification: e.target.value })}
        error={!!errors.sshCaJustification}
        helperText={
          errors.sshCaJustification ??
          'Context for the approver on why SSH CA access is needed.'
        }
      />
    </>
  );
}

// ─── Component ────────────────────────────────────────────────────────────────

export function VaultOnboardingForm({
  bsnOptions = [],
  isLoadingBsns = false,
  userOptions = [],
  isLoadingUsers = false,
  techLead,
  approvers,
  isLoadingTechLead = false,
  managers,
  isLoadingManagers = false,
  onUsersChange,
  onBsnChange,
  isSeEligible: _isSeEligible = false,
  initialValues,
  onSubmit,
  isSubmitting = false,
  onValuesChange,
  opaDown = false,
}: VaultOnboardingFormProps) {
  const classes = useStyles();

  const [values, setValues] = React.useState<VaultOnboardingFormValues>({
    bsns: [],
    applicationType: 'standard-service',
    deploymentTargets: [],
    requestType: 'new',
    accessType: '',
    capabilities: ['read', 'list', 'create', 'update'],

    environment: [],
    users: [],
    forgeRockOwner: null,
    justification: '',
    delegatedAccess: [],
    sharedSecretConsumers: [],
    targetClusters: DEFAULT_CLUSTERS,
    additionalEngines: [],
    enginesByCluster: {},
    pki: null,
    database: null,
    awsIam: null,
    sshCa: null,
    engineApplyToAll: {
      pki: true,
      database: true,
      aws_iam: true,
      ssh_ca: true,
    },
    pkiByCluster: {},
    databaseByCluster: {},
    awsIamByCluster: {},
    sshCaByCluster: {},
    ...initialValues,
  });

  const [errors, setErrors] = React.useState<FieldErrors>({});

  // Per-datacenter field errors, keyed by clusterId, populated by validate()
  // when an engine's apply-to-all toggle is off. A cluster's entry can carry
  // errors for more than one engine at once (e.g. PKI and Database both
  // per-datacenter) since FieldErrors keys are already engine-prefixed.
  const [errorsByCluster, setErrorsByCluster] = React.useState<
    Record<string, FieldErrors>
  >({});

  // Per-row validation errors for the delegated-access grants, indexed to match
  // values.delegatedAccess. Kept separate from the flat `errors` map above.
  const [delegatedErrors, setDelegatedErrors] = React.useState<
    { subpath?: string; group?: string; capabilities?: string }[]
  >([]);

  // Per-row validation errors for the shared-secret consumer grants, indexed
  // to match values.sharedSecretConsumers. Kept separate from the flat
  // `errors` map above, mirroring delegatedErrors.
  const [sharedSecretErrors, setSharedSecretErrors] = React.useState<
    { ownerBsn?: string; secretName?: string }[]
  >([]);

  // Fire onValuesChange on every state update so the scaffolder field extension
  // receives live values without waiting for form submit.
  React.useEffect(() => {
    onValuesChange?.(values);
  }, [values, onValuesChange]);

  // ── Derived state ───────────────────────────────────────────────────────────

  // SE path capabilities section is shown when at least one SE-eligible user
  // is in the users list.
  const hasSeUsers = values.users.some(u => u.seEligible);

  // k8s detection: deployment-target-based (replaces applicationType === 'kubernetes-workload').
  const isK8sDeployment =
    values.deploymentTargets.includes('k8s-onprem') ||
    values.deploymentTargets.includes('k8s-cloud');

  const isMachineOnly = values.accessType === 'machine';

  // Role/TTL derived from deploymentTargets — passed into PkiConfigFields
  // (once for the shared box, once per datacenter box); the rest of PKI's
  // derived state now lives inside PkiConfigFields itself, computed from
  // whichever config object it's given.
  const pkiRoleName = derivePkiRoleName(values.deploymentTargets);
  const pkiDefaultTtl = pkiDefaultMaxTtl(pkiRoleName);

  // ── Handlers ────────────────────────────────────────────────────────────────

  const handleDeploymentTargetToggle = (target: DeploymentTarget) => {
    setValues(v => ({
      ...v,
      deploymentTargets: v.deploymentTargets.includes(target)
        ? v.deploymentTargets.filter(t => t !== target)
        : [...v.deploymentTargets, target],
    }));
  };

  const handleCapabilityToggle = (cap: Capability) => {
    setValues(v => {
      const next = v.capabilities.includes(cap)
        ? v.capabilities.filter(c => c !== cap)
        : [...v.capabilities, cap];
      return { ...v, capabilities: next };
    });
  };

  const handleUsersChange = (
    _: React.ChangeEvent<object>,
    selected: OwnerOption[],
  ) => {
    setValues(v => {
      // Keep the ForgeRock Owner selection only if it's still a valid
      // candidate — either still in the (new) users list, or the tech lead
      // (who isn't affected by the users list at all).
      const ownerStillValid =
        !v.forgeRockOwner ||
        selected.some(u => u.id === v.forgeRockOwner!.id) ||
        (!!techLead &&
          v.forgeRockOwner!.label.trim().toLowerCase() ===
            techLead.trim().toLowerCase());
      return {
        ...v,
        users: selected,
        forgeRockOwner: ownerStillValid ? v.forgeRockOwner : null,
      };
    });
    onUsersChange?.(selected);
  };

  const handleForgeRockOwnerChange = (
    _: React.ChangeEvent<object>,
    selected: OwnerOption | null,
  ) => {
    setValues(v => ({ ...v, forgeRockOwner: selected }));
  };

  // ForgeRock Owner candidates: the tech lead (a display-only string, no id —
  // synthesized into an OwnerOption with a namespaced id) plus whoever is in
  // the users list. Deduped by label so the tech lead doesn't appear twice if
  // also manually added as a user.
  const forgeRockOwnerCandidates: OwnerOption[] = [
    ...(techLead &&
    !values.users.some(
      u => u.label.trim().toLowerCase() === techLead.trim().toLowerCase(),
    )
      ? [{ id: `techlead:${techLead}`, label: techLead, type: 'user' as const }]
      : []),
    ...values.users,
  ];

  // Removes a datacenter's entry from a per-cluster config map, or seeds one
  // with a fresh default config — shared by all four engines below.
  function withClusterEntry<T>(
    map: Record<string, T | null>,
    clusterId: string,
    removing: boolean,
    makeDefault: () => T,
  ): Record<string, T | null> {
    if (removing) {
      return Object.fromEntries(
        Object.entries(map).filter(([id]) => id !== clusterId),
      );
    }
    return { ...map, [clusterId]: map[clusterId] ?? makeDefault() };
  }

  const handleEngineToggle = (clusterId: string, engine: OptInEngine) => {
    setValues(v => {
      const clusterEngines = v.enginesByCluster[clusterId] ?? [];
      const removing = clusterEngines.includes(engine);
      const nextCluster = removing
        ? clusterEngines.filter(e => e !== engine)
        : [...clusterEngines, engine];
      const nextByCluster = { ...v.enginesByCluster, [clusterId]: nextCluster };
      const nextEngines = [
        ...new Set(Object.values(nextByCluster).flat()),
      ] as OptInEngine[];
      const wasActive = v.additionalEngines.includes(engine);
      const isActive = nextEngines.includes(engine);

      return {
        ...v,
        enginesByCluster: nextByCluster,
        additionalEngines: nextEngines,
        pki:
          !isActive && wasActive && engine === 'pki'
            ? null
            : isActive && !wasActive && engine === 'pki'
              ? defaultPkiConfig()
              : v.pki,
        database:
          !isActive && wasActive && engine === 'database'
            ? null
            : isActive && !wasActive && engine === 'database'
              ? defaultDatabaseConfig()
              : v.database,
        awsIam:
          !isActive && wasActive && engine === 'aws_iam'
            ? null
            : isActive && !wasActive && engine === 'aws_iam'
              ? defaultAwsIamConfig()
              : v.awsIam,
        sshCa:
          !isActive && wasActive && engine === 'ssh_ca'
            ? null
            : isActive && !wasActive && engine === 'ssh_ca'
              ? defaultSshCaConfig()
              : v.sshCa,
        // Per-datacenter mode: this cluster gets/loses its own config entry
        // the moment the engine is checked/unchecked for it. No-op while
        // apply-to-all is on for that engine (the map goes unused then).
        pkiByCluster:
          engine === 'pki' && !v.engineApplyToAll.pki
            ? withClusterEntry(
                v.pkiByCluster,
                clusterId,
                removing,
                defaultPkiConfig,
              )
            : v.pkiByCluster,
        databaseByCluster:
          engine === 'database' && !v.engineApplyToAll.database
            ? withClusterEntry(
                v.databaseByCluster,
                clusterId,
                removing,
                defaultDatabaseConfig,
              )
            : v.databaseByCluster,
        awsIamByCluster:
          engine === 'aws_iam' && !v.engineApplyToAll.aws_iam
            ? withClusterEntry(
                v.awsIamByCluster,
                clusterId,
                removing,
                defaultAwsIamConfig,
              )
            : v.awsIamByCluster,
        sshCaByCluster:
          engine === 'ssh_ca' && !v.engineApplyToAll.ssh_ca
            ? withClusterEntry(
                v.sshCaByCluster,
                clusterId,
                removing,
                defaultSshCaConfig,
              )
            : v.sshCaByCluster,
      };
    });
  };

  // Datacenters (among the selected targets) that currently have `engine`
  // checked — drives both the per-cluster config-box list and whether the
  // apply-to-all toggle is meaningful (needs 2+ to matter).
  const clustersWithEngine = (engine: OptInEngine) =>
    values.targetClusters.filter(c =>
      (values.enginesByCluster[c] ?? []).includes(engine),
    );

  const handleEngineApplyToAllToggle = (engine: OptInEngine) => {
    setValues(v => {
      const applyToAll = !v.engineApplyToAll[engine];
      if (applyToAll) {
        // Switching back on: leave the per-cluster maps as they are (cached,
        // non-destructive) — the shared config is what's used while on.
        return {
          ...v,
          engineApplyToAll: { ...v.engineApplyToAll, [engine]: applyToAll },
        };
      }
      // Switching off: seed every datacenter currently checked for this
      // engine with a clone of the shared config, so requesters start from
      // what they already had instead of blank boxes.
      const clusters = clustersWithEngine(engine);
      const seed = <T,>(
        map: Record<string, T | null>,
        shared: T | null,
      ): Record<string, T | null> => {
        const next = { ...map };
        clusters.forEach(clusterId => {
          if (!next[clusterId] && shared) next[clusterId] = { ...shared };
        });
        return next;
      };
      return {
        ...v,
        engineApplyToAll: { ...v.engineApplyToAll, [engine]: applyToAll },
        pkiByCluster:
          engine === 'pki' ? seed(v.pkiByCluster, v.pki) : v.pkiByCluster,
        databaseByCluster:
          engine === 'database'
            ? seed(v.databaseByCluster, v.database)
            : v.databaseByCluster,
        awsIamByCluster:
          engine === 'aws_iam'
            ? seed(v.awsIamByCluster, v.awsIam)
            : v.awsIamByCluster,
        sshCaByCluster:
          engine === 'ssh_ca'
            ? seed(v.sshCaByCluster, v.sshCa)
            : v.sshCaByCluster,
      };
    });
  };

  const handlePkiChange = (patch: Partial<PkiConfig>, clusterId?: string) => {
    setValues(v => {
      if (clusterId) {
        const current = v.pkiByCluster[clusterId];
        return current
          ? {
              ...v,
              pkiByCluster: {
                ...v.pkiByCluster,
                [clusterId]: { ...current, ...patch },
              },
            }
          : v;
      }
      return v.pki ? { ...v, pki: { ...v.pki, ...patch } } : v;
    });
  };

  const handleDatabaseChange = (
    patch: Partial<DatabaseConfig>,
    clusterId?: string,
  ) => {
    setValues(v => {
      if (clusterId) {
        const current = v.databaseByCluster[clusterId];
        return current
          ? {
              ...v,
              databaseByCluster: {
                ...v.databaseByCluster,
                [clusterId]: { ...current, ...patch },
              },
            }
          : v;
      }
      return v.database ? { ...v, database: { ...v.database, ...patch } } : v;
    });
  };

  const handleAwsIamChange = (
    patch: Partial<AwsIamConfig>,
    clusterId?: string,
  ) => {
    setValues(v => {
      if (clusterId) {
        const current = v.awsIamByCluster[clusterId];
        return current
          ? {
              ...v,
              awsIamByCluster: {
                ...v.awsIamByCluster,
                [clusterId]: { ...current, ...patch },
              },
            }
          : v;
      }
      return v.awsIam ? { ...v, awsIam: { ...v.awsIam, ...patch } } : v;
    });
  };

  const handleSshCaChange = (
    patch: Partial<SshCaConfig>,
    clusterId?: string,
  ) => {
    setValues(v => {
      if (clusterId) {
        const current = v.sshCaByCluster[clusterId];
        return current
          ? {
              ...v,
              sshCaByCluster: {
                ...v.sshCaByCluster,
                [clusterId]: { ...current, ...patch },
              },
            }
          : v;
      }
      return v.sshCa ? { ...v, sshCa: { ...v.sshCa, ...patch } } : v;
    });
  };

  const handleAddDelegatedAccess = () => {
    setValues(v => ({
      ...v,
      delegatedAccess: [
        ...v.delegatedAccess,
        { subpath: '', group: '', capabilities: ['read', 'list'] },
      ],
    }));
  };

  const handleRemoveDelegatedAccess = (index: number) => {
    setValues(v => ({
      ...v,
      delegatedAccess: v.delegatedAccess.filter((_, i) => i !== index),
    }));
    setDelegatedErrors(errs => errs.filter((_, i) => i !== index));
  };

  const handleDelegatedAccessChange = (
    index: number,
    patch: Partial<DelegatedAccessGrant>,
  ) => {
    setValues(v => ({
      ...v,
      delegatedAccess: v.delegatedAccess.map((grant, i) =>
        i === index ? { ...grant, ...patch } : grant,
      ),
    }));
  };

  const handleAddSharedSecretConsumer = () => {
    setValues(v => ({
      ...v,
      sharedSecretConsumers: [
        ...v.sharedSecretConsumers,
        { ownerBsn: '', secretName: '', accessLevel: 'ro' },
      ],
    }));
  };

  const handleRemoveSharedSecretConsumer = (index: number) => {
    setValues(v => ({
      ...v,
      sharedSecretConsumers: v.sharedSecretConsumers.filter(
        (_, i) => i !== index,
      ),
    }));
    setSharedSecretErrors(errs => errs.filter((_, i) => i !== index));
  };

  const handleSharedSecretConsumerChange = (
    index: number,
    patch: Partial<SharedSecretConsumerGrant>,
  ) => {
    setValues(v => ({
      ...v,
      sharedSecretConsumers: v.sharedSecretConsumers.map((grant, i) =>
        i === index ? { ...grant, ...patch } : grant,
      ),
    }));
  };

  // ── Validation ──────────────────────────────────────────────────────────────

  // Sub-path segment: alphanumerics plus - _ . and internal slashes; no leading
  // slash, no spaces. Mirrors the kv_access.delegated_access[].subpath contract.
  const SUBPATH_PATTERN = /^[a-zA-Z0-9._-]+(\/[a-zA-Z0-9._-]+)*$/;

  const validate = (): boolean => {
    const next: typeof errors = {};

    if (values.bsns.length === 0)
      next.bsns = 'Select at least one application BSN';
    if (values.deploymentTargets.length === 0)
      next.deploymentTargets = 'Select at least one deployment target';
    if (!values.accessType) next.accessType = 'Select an access model';
    if (values.environment.length === 0)
      next.environment = 'Select at least one environment';
    if (values.accessType !== 'machine' && values.users.length === 0)
      next.users = 'At least one user is required';
    if (!values.forgeRockOwner)
      next.forgeRockOwner = 'Select a ForgeRock owner';
    if (values.justification.trim().length < 20)
      next.justification =
        'Business justification must be at least 20 characters';

    if (values.targetClusters.length < MIN_CLUSTERS)
      next.targetClusters = `Select at least ${MIN_CLUSTERS} target datacenters`;

    // Delegated access — validate each grant only when rows exist (optional section)
    const delegatedNext: typeof delegatedErrors = values.delegatedAccess.map(
      grant => {
        const rowErrors: {
          subpath?: string;
          group?: string;
          capabilities?: string;
        } = {};
        const subpath = grant.subpath.trim();
        if (!subpath) rowErrors.subpath = 'Sub-path is required';
        else if (!SUBPATH_PATTERN.test(subpath))
          rowErrors.subpath =
            'Use letters, numbers, - _ . and / (no leading slash or spaces)';
        if (!grant.group.trim()) rowErrors.group = 'Group is required';
        if (grant.capabilities.length === 0)
          rowErrors.capabilities = 'Select at least one capability';
        return rowErrors;
      },
    );
    setDelegatedErrors(delegatedNext);
    const delegatedValid = delegatedNext.every(
      r => Object.keys(r).length === 0,
    );

    // Shared-secret consumer opt-in — validate each grant only when rows
    // exist (optional section), mirroring delegated-access validation above.
    const sharedSecretNext: typeof sharedSecretErrors =
      values.sharedSecretConsumers.map(grant => {
        const rowErrors: { ownerBsn?: string; secretName?: string } = {};
        if (!grant.ownerBsn.trim())
          rowErrors.ownerBsn = 'Owner BSN is required';
        if (!grant.secretName.trim())
          rowErrors.secretName = 'Secret name is required';
        return rowErrors;
      });
    setSharedSecretErrors(sharedSecretNext);
    const sharedSecretValid = sharedSecretNext.every(
      r => Object.keys(r).length === 0,
    );

    // Engine config validation — shared config validated once when
    // apply-to-all is on; otherwise each per-datacenter config is validated
    // independently and its errors land in nextByCluster under that
    // datacenter, not in the flat `next` map.
    const nextByCluster: Record<string, FieldErrors> = {};
    const mergeClusterErrors = (clusterId: string, errs: FieldErrors) => {
      if (Object.keys(errs).length === 0) return;
      nextByCluster[clusterId] = { ...nextByCluster[clusterId], ...errs };
    };

    if (values.additionalEngines.includes('pki')) {
      if (values.engineApplyToAll.pki) {
        if (values.pki)
          Object.assign(next, validatePkiConfig(values.pki, pkiRoleName));
      } else {
        clustersWithEngine('pki').forEach(clusterId => {
          const cfg = values.pkiByCluster[clusterId];
          if (cfg)
            mergeClusterErrors(clusterId, validatePkiConfig(cfg, pkiRoleName));
        });
      }
    }

    if (values.additionalEngines.includes('database')) {
      if (values.engineApplyToAll.database) {
        if (values.database)
          Object.assign(next, validateDatabaseConfig(values.database));
      } else {
        clustersWithEngine('database').forEach(clusterId => {
          const cfg = values.databaseByCluster[clusterId];
          if (cfg) mergeClusterErrors(clusterId, validateDatabaseConfig(cfg));
        });
      }
    }

    if (values.additionalEngines.includes('aws_iam')) {
      if (values.engineApplyToAll.aws_iam) {
        if (values.awsIam)
          Object.assign(next, validateAwsIamConfig(values.awsIam));
      } else {
        clustersWithEngine('aws_iam').forEach(clusterId => {
          const cfg = values.awsIamByCluster[clusterId];
          if (cfg) mergeClusterErrors(clusterId, validateAwsIamConfig(cfg));
        });
      }
    }

    if (values.additionalEngines.includes('ssh_ca')) {
      if (values.engineApplyToAll.ssh_ca) {
        if (values.sshCa)
          Object.assign(next, validateSshCaConfig(values.sshCa));
      } else {
        clustersWithEngine('ssh_ca').forEach(clusterId => {
          const cfg = values.sshCaByCluster[clusterId];
          if (cfg) mergeClusterErrors(clusterId, validateSshCaConfig(cfg));
        });
      }
    }

    setErrors(next);
    setErrorsByCluster(nextByCluster);
    return (
      Object.keys(next).length === 0 &&
      Object.keys(nextByCluster).length === 0 &&
      delegatedValid &&
      sharedSecretValid
    );
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    // Defense in depth alongside the pointerEvents:'none' gray-out below —
    // that CSS doesn't stop a programmatic submit (e.g. Enter-key-in-field),
    // so the handler itself also short-circuits while OPA is down.
    if (opaDown) return;
    if (validate()) onSubmit?.(values);
  };

  // ── Render ──────────────────────────────────────────────────────────────────

  // Engine availability: an engine is offered only when ALL selected clusters support it.
  const availableEngines: OptInEngine[] =
    values.targetClusters.length === 0
      ? OPT_IN_ENGINES.map(e => e.value)
      : OPT_IN_ENGINES.map(e => e.value).filter(engine =>
          values.targetClusters.every(dc =>
            CLUSTER_ENGINE_AVAILABILITY[dc]?.includes(engine),
          ),
        );

  return (
    <form onSubmit={handleSubmit} className={classes.root} noValidate>
      {opaDown && (
        <Alert severity="error">
          Policy engine unavailable — onboarding is temporarily disabled. Please
          try again shortly.
        </Alert>
      )}
      <div
        data-testid="onboarding-form-fields"
        style={opaDown ? { opacity: 0.4, pointerEvents: 'none' } : undefined}
      >
        <Typography variant="h5">Request BSN Vault Estate</Typography>

        {/* ── Target Datacenters (0731 demo feedback: pulled to the top) ───── */}
        <Paper variant="outlined" className={classes.card}>
          <Typography variant="subtitle1" className={classes.cardTitle}>
            Target Datacenters
          </Typography>

          <FormControl
            variant="outlined"
            fullWidth
            error={!!errors.targetClusters}
            disabled={values.bsns.length === 0}
          >
            <InputLabel id="datacenters-label">Target Datacenters</InputLabel>
            <Select
              labelId="datacenters-label"
              label="Target Datacenters"
              multiple
              value={values.targetClusters}
              onChange={e => {
                const selected = e.target.value as string[];
                if (selected.length <= MAX_CLUSTERS) {
                  setValues(v => {
                    const nextByCluster = Object.fromEntries(
                      Object.entries(v.enginesByCluster).filter(([k]) =>
                        selected.includes(k),
                      ),
                    );
                    return {
                      ...v,
                      targetClusters: selected,
                      enginesByCluster: nextByCluster,
                      additionalEngines: [
                        ...new Set(Object.values(nextByCluster).flat()),
                      ] as OptInEngine[],
                    };
                  });
                }
              }}
              renderValue={selected => (
                <div className={classes.chips}>
                  {(selected as string[]).map(id => {
                    const dc = DATACENTERS.find(d => d.id === id)!;
                    return (
                      <Chip
                        key={id}
                        label={dc.label}
                        size="small"
                        onDelete={
                          values.targetClusters.length > MIN_CLUSTERS
                            ? () =>
                                setValues(v => {
                                  const remaining = v.targetClusters.filter(
                                    c => c !== id,
                                  );
                                  const nextByCluster = Object.fromEntries(
                                    Object.entries(v.enginesByCluster).filter(
                                      ([k]) => remaining.includes(k),
                                    ),
                                  );
                                  return {
                                    ...v,
                                    targetClusters: remaining,
                                    enginesByCluster: nextByCluster,
                                    additionalEngines: [
                                      ...new Set(
                                        Object.values(nextByCluster).flat(),
                                      ),
                                    ] as OptInEngine[],
                                  };
                                })
                            : undefined
                        }
                        onMouseDown={e => e.stopPropagation()}
                      />
                    );
                  })}
                </div>
              )}
            >
              {DATACENTERS.map(dc => (
                <MenuItem
                  key={dc.id}
                  value={dc.id}
                  disabled={
                    values.targetClusters.length >= MAX_CLUSTERS &&
                    !values.targetClusters.includes(dc.id)
                  }
                >
                  {dc.label}
                </MenuItem>
              ))}
            </Select>
            <FormHelperText>
              {errors.targetClusters ??
                `Select ${MIN_CLUSTERS}–${MAX_CLUSTERS} datacenters. Policy is deployed only to selected clusters.`}
            </FormHelperText>
          </FormControl>
        </Paper>

        {/* ── Application Details ─────────────────────────────────────────── */}
        <Paper variant="outlined" className={classes.card}>
          <Typography variant="subtitle1" className={classes.cardTitle}>
            Application Details
          </Typography>

          {/* BSN — multi-select; in-progress BSNs are shown greyed and non-selectable */}
          <Autocomplete
            id="bsn"
            multiple
            options={bsnOptions}
            loading={isLoadingBsns}
            disabled={isLoadingBsns}
            getOptionLabel={option => option.label}
            getOptionSelected={(option, value) => option.id === value.id}
            getOptionDisabled={option => !!option.inProgress}
            value={bsnOptions.filter(o => values.bsns.includes(o.id))}
            onChange={(_, selected) => {
              const bsns = selected.map(o => o.id);
              setValues(v => ({ ...v, bsns }));
              onBsnChange?.(bsns[0] ?? '');
            }}
            renderOption={option => (
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 8,
                  opacity: option.inProgress ? 0.5 : 1,
                }}
              >
                <span>{option.label}</span>
                {option.inProgress && (
                  <Chip
                    label="In progress"
                    size="small"
                    style={{ height: 18, fontSize: '0.65rem' }}
                  />
                )}
              </div>
            )}
            renderInput={params => (
              <TextField
                {...params}
                label="Application (BSN)"
                variant="outlined"
                placeholder={
                  values.bsns.length === 0 ? 'Search applications…' : ''
                }
                error={!!errors.bsns}
                helperText={
                  errors.bsns ??
                  (isLoadingBsns
                    ? 'Loading applications from ServiceNow…'
                    : 'Select one or more BSNs — same Vault config applies to each. Applications in progress are not selectable.')
                }
                InputProps={{
                  ...params.InputProps,
                  endAdornment: (
                    <>
                      {isLoadingBsns && <CircularProgress size={18} />}
                      {params.InputProps.endAdornment}
                    </>
                  ),
                }}
              />
            )}
          />

          {/* Deployment Target — additive, agreed July 31, 2026 (code-review/showcase).
            k8s detection now uses isK8sDeployment (derived from this field) rather than
            the Application Type selector, which is no longer shown in the UI. */}
          <FormControl
            component="fieldset"
            error={!!errors.deploymentTargets}
            disabled={values.bsns.length === 0}
          >
            <FormLabel component="legend">
              My application is deployed on
            </FormLabel>
            <FormGroup row>
              {DEPLOYMENT_TARGETS.map(({ value, label }) => (
                <FormControlLabel
                  key={value}
                  control={
                    <Checkbox
                      checked={values.deploymentTargets.includes(value)}
                      onChange={() => handleDeploymentTargetToggle(value)}
                      color="primary"
                    />
                  }
                  label={label}
                />
              ))}
            </FormGroup>
            <FormHelperText>
              {errors.deploymentTargets ??
                'Select every platform this application runs on'}
            </FormHelperText>
          </FormControl>

          {/* Kubernetes redirect — gray out remaining form and link to NaaS */}
          {isK8sDeployment && (
            <Alert severity="info" style={{ marginTop: 8 }}>
              Kubernetes / OpenShift workloads use the NaaS onboarding form. The
              fields below do not apply.
            </Alert>
          )}

          {/* Access Type — 0731 demo feedback: moved up here (ahead of the Users
            field in Ownership) since it determines what user selection means. */}
          <FormControl
            component="fieldset"
            error={!!errors.accessType}
            disabled={values.bsns.length === 0}
          >
            <FormLabel component="legend">Access Model</FormLabel>
            <RadioGroup
              aria-label="access model"
              name="accessType"
              value={values.accessType}
              onChange={e => {
                const accessType = e.target.value as AccessType;
                setValues(v => ({
                  ...v,
                  accessType,
                  // Machine-only access has no human component — clear any
                  // previously selected users.
                  users: accessType === 'machine' ? [] : v.users,
                }));
                if (accessType === 'machine') {
                  // Clear any stale "At least one user is required" error from a
                  // prior failed submit — it no longer applies once the Users
                  // field is disabled for machine-only access.
                  setErrors(errs => ({ ...errs, users: undefined }));
                  if (values.users.length > 0) {
                    onUsersChange?.([]);
                  }
                }
              }}
              row
            >
              <FormControlLabel
                value="machine"
                label="Machine only"
                control={<Radio color="primary" />}
              />
              <FormControlLabel
                value="both"
                label="Machine + Human"
                control={<Radio color="primary" />}
              />
            </RadioGroup>
            <FormHelperText>
              {errors.accessType ??
                'Machine only: provisions machine KV policy and cert auth role. ' +
                  'Machine + Human: also provisions a human KV policy (read + list only) and ForgeRock LDAP groups for this BSN.'}
            </FormHelperText>
          </FormControl>

          {/* Environment */}
          <FormControl
            variant="outlined"
            fullWidth
            error={!!errors.environment}
            disabled={values.bsns.length === 0}
          >
            <InputLabel id="environment-label">Environment</InputLabel>
            <Select
              labelId="environment-label"
              id="environment"
              label="Environment"
              multiple
              value={values.environment}
              onChange={e =>
                setValues(v => ({
                  ...v,
                  environment: e.target.value as Environment[],
                }))
              }
              renderValue={selected => (
                <div className={classes.chips}>
                  {(selected as Environment[]).map(val => {
                    const env = ENVIRONMENTS.find(e => e.value === val)!;
                    return <Chip key={val} label={env.label} size="small" />;
                  })}
                </div>
              )}
            >
              {ENVIRONMENTS.map(({ value, label }) => (
                <MenuItem key={value} value={value}>
                  {label}
                </MenuItem>
              ))}
            </Select>
            <FormHelperText>
              {errors.environment ??
                'Select all environments this application needs access to'}
            </FormHelperText>
          </FormControl>

          {/* Business Justification */}
          <TextField
            id="justification"
            label="Business Justification"
            disabled={values.bsns.length === 0}
            placeholder="Why does this application need Vault KV access?"
            fullWidth
            multiline
            rows={4}
            variant="outlined"
            value={values.justification}
            onChange={e =>
              setValues(v => ({ ...v, justification: e.target.value }))
            }
            error={!!errors.justification}
            helperText={errors.justification}
          />
        </Paper>

        {/* ── Ownership ───────────────────────────────────────────────────── */}
        <Paper
          variant="outlined"
          className={classes.card}
          style={
            isK8sDeployment
              ? { opacity: 0.4, pointerEvents: 'none' }
              : undefined
          }
        >
          <Typography variant="subtitle1" className={classes.cardTitle}>
            Ownership
          </Typography>

          {/* Tech Lead — auto-populated from ServiceNow BSN cache */}
          <div className={classes.readOnlyBlock}>
            <Typography className={classes.readOnlyLabel}>Tech Lead</Typography>
            <div className={classes.readOnlyValue}>
              <Typography
                variant="body2"
                color={techLead ? 'textPrimary' : 'textSecondary'}
              >
                {values.bsns.length === 0
                  ? 'Select a BSN to load the tech lead'
                  : isLoadingTechLead
                    ? 'Loading…'
                    : (techLead ?? 'No tech lead on record for this BSN')}
              </Typography>
            </div>
            <FormHelperText>
              Retrieved from ServiceNow — technical point of contact for this
              application
            </FormHelperText>
          </div>

          {/* Users — editable multi-select */}
          <Autocomplete
            multiple
            options={userOptions}
            loading={isLoadingUsers}
            disabled={values.bsns.length === 0 || isMachineOnly}
            getOptionLabel={option => option.label}
            getOptionSelected={(option, value) => option.id === value.id}
            value={values.users}
            onChange={handleUsersChange}
            renderTags={(selected, getTagProps) =>
              selected.map((option, index) => (
                <Chip
                  key={option.id}
                  label={
                    option.seEligible ? `${option.label} · SE` : option.label
                  }
                  size="small"
                  icon={<PersonIcon fontSize="small" />}
                  color={option.seEligible ? 'primary' : 'default'}
                  {...getTagProps({ index })}
                />
              ))
            }
            renderOption={option => (
              <div className={classes.ownerOption}>
                <PersonIcon fontSize="small" color="action" />
                <span className={classes.ownerOptionLabel}>{option.label}</span>
                {option.seEligible && (
                  <Chip
                    label="SE"
                    size="small"
                    className={classes.capabilityChip}
                    color="primary"
                  />
                )}
              </div>
            )}
            renderInput={params => (
              <TextField
                {...params}
                label="Users"
                variant="outlined"
                placeholder={
                  values.users.length === 0 ? 'Search users…' : undefined
                }
                error={!!errors.users}
                helperText={
                  errors.users ??
                  (isMachineOnly
                    ? 'Not applicable for machine-only access'
                    : "Users who will have access to this application's Vault secrets")
                }
                InputProps={{
                  ...params.InputProps,
                  endAdornment: (
                    <>
                      {isLoadingUsers && <CircularProgress size={18} />}
                      {params.InputProps.endAdornment}
                    </>
                  ),
                }}
              />
            )}
          />

          {/* Managers — auto-derived from ForgeRock, shown when users are selected */}
          {values.users.length > 0 && (
            <div className={classes.readOnlyBlock}>
              <Typography className={classes.readOnlyLabel}>
                Managers
              </Typography>
              {isLoadingManagers ? (
                <div className={classes.readOnlyValue}>
                  <CircularProgress size={18} />
                </div>
              ) : managers && managers.length > 0 ? (
                <div className={classes.chipList}>
                  {managers.map(m => (
                    <Chip
                      key={m}
                      label={m}
                      size="small"
                      icon={<PersonIcon fontSize="small" />}
                    />
                  ))}
                </div>
              ) : (
                <div className={classes.readOnlyValue}>
                  <Typography variant="body2" color="textSecondary">
                    No managers found in ForgeRock
                  </Typography>
                </div>
              )}
              <FormHelperText>
                Resolved from ForgeRock — deduplicated union of all selected
                users' managers. Stage 1 approvers in ServiceNow.
              </FormHelperText>
            </div>
          )}

          {/* Technical Approvers Group — from ServiceNow CMDB
            technical_approval_groups. 0731 demo feedback (Speaker 3): the
            chain reads as Manager -> Technical Approvers Group, falling
            back to Tech Lead only when no technical approvers group is on
            record for the BSN. */}
          <div className={classes.readOnlyBlock}>
            <Typography className={classes.readOnlyLabel}>
              Technical Approvers Group
            </Typography>
            {approvers && approvers.length > 0 ? (
              <div className={classes.chipList}>
                {approvers.map(a => (
                  <Chip key={a} label={a} size="small" />
                ))}
              </div>
            ) : (
              <div className={classes.readOnlyValue}>
                <Typography
                  variant="body2"
                  className={
                    values.bsns.length > 0 ? classes.warningText : undefined
                  }
                  color={values.bsns.length > 0 ? undefined : 'textSecondary'}
                >
                  {values.bsns.length > 0
                    ? 'No technical approvers group on record for this BSN — approval falls back to Tech Lead'
                    : 'Select a BSN to load the technical approvers group'}
                </Typography>
              </div>
            )}
            <FormHelperText>
              From ServiceNow CMDB — Stage 2: Technical Approvers Group
              approval, falling back to Tech Lead when none is on record for
              this application
            </FormHelperText>
          </div>

          {/* ForgeRock Owner — single-select over tech lead + selected users */}
          <Autocomplete
            options={forgeRockOwnerCandidates}
            disabled={values.bsns.length === 0}
            getOptionLabel={option => option.label}
            getOptionSelected={(option, value) => option.id === value.id}
            value={values.forgeRockOwner}
            onChange={handleForgeRockOwnerChange}
            renderInput={params => (
              <TextField
                {...params}
                label="ForgeRock Owner"
                variant="outlined"
                placeholder="Search tech lead or selected users…"
                error={!!errors.forgeRockOwner}
                helperText={
                  errors.forgeRockOwner ??
                  'The person selected here will be the designated owner of the group in ForgeRock.'
                }
              />
            )}
          />
        </Paper>

        {/* ── Vault Access Configuration ───────────────────────────────────── */}
        <Paper
          variant="outlined"
          className={classes.card}
          style={
            isK8sDeployment
              ? { opacity: 0.4, pointerEvents: 'none' }
              : undefined
          }
        >
          <Typography variant="subtitle1" className={classes.cardTitle}>
            Vault Access Configuration
          </Typography>

          {/* ── Default Engines (always provisioned) ────────────────────────── */}
          <FormControl component="fieldset">
            <FormLabel component="legend">Default Engines</FormLabel>
            <FormGroup row>
              <FormControlLabel
                disabled
                control={<Checkbox checked color="primary" />}
                label="KV"
              />
              <FormControlLabel
                disabled
                control={<Checkbox checked color="primary" />}
                label="Artifactory"
              />
            </FormGroup>
            <FormHelperText>
              KV is always provisioned. Artifactory is auto-applied via BSN →
              business-segment lookup in AAP; see the note below if you've
              selected more than one deployment target.
            </FormHelperText>
            {/* Artifactory is OCP-only for now (Decision 21/33, GH #605) — only
              trigger the warning for the confirmed k8s-onprem case. Whether
              this also applies to k8s-cloud is an open question
              (ARTIFACTORY-CLOUD-SCOPE); don't warn on a guess. */}
            {values.deploymentTargets.includes('vm-onprem') &&
              values.deploymentTargets.includes('k8s-onprem') && (
                <Alert severity="warning" style={{ marginTop: 8 }}>
                  Artifactory is available for your Kubernetes environment only.
                </Alert>
              )}
          </FormControl>

          {/* ── KV Permissions ───────────────────────────────────────────────── */}
          <FormControl component="fieldset" disabled={values.bsns.length === 0}>
            <FormLabel component="legend">KV Permissions</FormLabel>
            <FormGroup>
              {ALL_CAPABILITIES.map(({ value, label }) => {
                const checked = values.capabilities.includes(value);
                const disabled = values.bsns.length === 0;

                return (
                  <div key={value} className={classes.capabilityRow}>
                    <FormControlLabel
                      disabled={disabled}
                      control={
                        <Checkbox
                          checked={checked}
                          onChange={() => handleCapabilityToggle(value)}
                          color="primary"
                        />
                      }
                      label={label}
                    />
                    <Chip
                      label="default"
                      size="small"
                      className={classes.capabilityChip}
                    />
                  </div>
                );
              })}

              {/* delete — permanently disabled */}
              <div className={classes.capabilityRow}>
                <Tooltip title="Deleting secrets requires a separate change request to maintain an auditable deletion record">
                  <span>
                    <FormControlLabel
                      disabled
                      control={<Checkbox />}
                      label="Delete"
                    />
                  </span>
                </Tooltip>
                <Chip
                  label="change request required"
                  size="small"
                  className={classes.capabilityChip}
                />
              </div>
            </FormGroup>
            <FormHelperText>
              {values.bsns.length === 0
                ? 'Select a BSN to configure permissions'
                : 'Read and List are required. Create and Update are optional.'}
            </FormHelperText>
          </FormControl>

          {/* ── Delegated Access ─────────────────────────────────────────────── */}
          <FormControl
            component="fieldset"
            className={classes.delegatedSection}
            disabled={values.bsns.length === 0}
          >
            <FormLabel component="legend">Delegated Access</FormLabel>
            <FormHelperText>
              Optionally grant a specific group scoped access to a sub-path of
              this BSN&apos;s KV space (e.g. a shared database secret).
            </FormHelperText>

            {values.delegatedAccess.map((grant, index) => {
              const rowErr = delegatedErrors[index] ?? {};
              return (
                <div key={index} className={classes.delegatedGrant}>
                  <div className={classes.delegatedGrantHeader}>
                    <Typography className={classes.delegatedGrantTitle}>
                      Grant {index + 1}
                    </Typography>
                    <IconButton
                      aria-label={`Remove delegated access grant ${index + 1}`}
                      size="small"
                      onClick={() => handleRemoveDelegatedAccess(index)}
                    >
                      <DeleteOutlineIcon fontSize="small" />
                    </IconButton>
                  </div>

                  <TextField
                    id={`delegated-subpath-${index}`}
                    label="Sub-path"
                    placeholder="db"
                    fullWidth
                    variant="outlined"
                    value={grant.subpath}
                    onChange={e =>
                      handleDelegatedAccessChange(index, {
                        subpath: e.target.value,
                      })
                    }
                    error={!!rowErr.subpath}
                    helperText={
                      rowErr.subpath ??
                      "Relative to this BSN's KV path (e.g. db)"
                    }
                  />

                  <TextField
                    id={`delegated-group-${index}`}
                    label="Group"
                    placeholder="grp-dbplatform-readonly"
                    fullWidth
                    variant="outlined"
                    value={grant.group}
                    onChange={e =>
                      handleDelegatedAccessChange(index, {
                        group: e.target.value,
                      })
                    }
                    error={!!rowErr.group}
                    helperText={
                      rowErr.group ?? 'LDAP/AD group to delegate access to'
                    }
                  />

                  <FormControl
                    variant="outlined"
                    fullWidth
                    error={!!rowErr.capabilities}
                  >
                    <InputLabel id={`delegated-caps-label-${index}`}>
                      Permissions
                    </InputLabel>
                    <Select
                      labelId={`delegated-caps-label-${index}`}
                      label="Permissions"
                      multiple
                      value={grant.capabilities}
                      onChange={e =>
                        handleDelegatedAccessChange(index, {
                          capabilities: e.target.value as Capability[],
                        })
                      }
                      renderValue={selected => (
                        <div className={classes.chips}>
                          {(selected as Capability[]).map(val => {
                            const cap = ALL_CAPABILITIES.find(
                              c => c.value === val,
                            )!;
                            return (
                              <Chip key={val} label={cap.label} size="small" />
                            );
                          })}
                        </div>
                      )}
                    >
                      {ALL_CAPABILITIES.map(({ value, label }) => (
                        <MenuItem key={value} value={value}>
                          {label}
                        </MenuItem>
                      ))}
                    </Select>
                    <FormHelperText>
                      {rowErr.capabilities ??
                        'Permissions granted to the group on this sub-path'}
                    </FormHelperText>
                  </FormControl>
                </div>
              );
            })}

            <Button
              type="button"
              variant="outlined"
              size="small"
              color="primary"
              className={classes.addButton}
              startIcon={<AddIcon />}
              onClick={handleAddDelegatedAccess}
              disabled={values.bsns.length === 0}
            >
              Add delegated access
            </Button>
          </FormControl>

          {/* ── Shared Secret Access (Workflow A opt-in, GH #990/#991) ────────── */}
          <FormControl
            component="fieldset"
            className={classes.delegatedSection}
            disabled={values.bsns.length === 0}
          >
            <FormLabel component="legend">Shared Secret Access</FormLabel>
            <FormHelperText>
              Optionally consume a secret owned by another BSN. Read-only by
              default; read-write requires coordinated approval from both the
              owner and consumer BSN.
            </FormHelperText>

            {values.sharedSecretConsumers.map((grant, index) => {
              const rowErr = sharedSecretErrors[index] ?? {};
              return (
                <div key={index} className={classes.delegatedGrant}>
                  <div className={classes.delegatedGrantHeader}>
                    <Typography className={classes.delegatedGrantTitle}>
                      Shared Secret {index + 1}
                    </Typography>
                    <IconButton
                      aria-label={`Remove shared secret access grant ${
                        index + 1
                      }`}
                      size="small"
                      onClick={() => handleRemoveSharedSecretConsumer(index)}
                    >
                      <DeleteOutlineIcon fontSize="small" />
                    </IconButton>
                  </div>

                  <TextField
                    id={`shared-secret-owner-bsn-${index}`}
                    label="Owner BSN"
                    placeholder="BSN-1234"
                    fullWidth
                    variant="outlined"
                    value={grant.ownerBsn}
                    onChange={e =>
                      handleSharedSecretConsumerChange(index, {
                        ownerBsn: e.target.value,
                      })
                    }
                    error={!!rowErr.ownerBsn}
                    helperText={
                      rowErr.ownerBsn ?? 'The BSN that owns this secret'
                    }
                  />

                  <TextField
                    id={`shared-secret-name-${index}`}
                    label="Secret Name"
                    placeholder="apikey"
                    fullWidth
                    variant="outlined"
                    value={grant.secretName}
                    onChange={e =>
                      handleSharedSecretConsumerChange(index, {
                        secretName: e.target.value,
                      })
                    }
                    error={!!rowErr.secretName}
                    helperText={
                      rowErr.secretName ?? 'Name of the shared secret / path'
                    }
                  />

                  <FormControl variant="outlined" fullWidth>
                    <InputLabel
                      id={`shared-secret-access-level-label-${index}`}
                    >
                      Access Level
                    </InputLabel>
                    <Select
                      labelId={`shared-secret-access-level-label-${index}`}
                      label="Access Level"
                      value={grant.accessLevel}
                      onChange={e =>
                        handleSharedSecretConsumerChange(index, {
                          accessLevel: e.target
                            .value as SharedSecretAccessLevel,
                        })
                      }
                    >
                      <MenuItem value="ro">Read-only (default)</MenuItem>
                      <MenuItem value="rw">Read-write</MenuItem>
                    </Select>
                    <FormHelperText>
                      {grant.accessLevel === 'rw'
                        ? 'Requires coordinated owner + consumer approval (SNOW UC3).'
                        : 'Default access level for consuming a shared secret.'}
                    </FormHelperText>
                  </FormControl>
                </div>
              );
            })}

            <Button
              type="button"
              variant="outlined"
              size="small"
              color="primary"
              className={classes.addButton}
              startIcon={<AddIcon />}
              onClick={handleAddSharedSecretConsumer}
              disabled={values.bsns.length === 0}
            >
              Add shared secret access
            </Button>
          </FormControl>

          {/* SE User Permissions — shown when any SE-eligible user is in the list */}
          {hasSeUsers && (
            <FormControl component="fieldset">
              <FormLabel component="legend">SE User Permissions</FormLabel>
              <FormGroup>
                {SE_PATH_CAPABILITIES.map(({ path, label, capability }) => (
                  <div key={path} className={classes.capabilityRow}>
                    <FormControlLabel
                      disabled
                      control={<Checkbox checked color="primary" />}
                      label={`${label} (${path})`}
                    />
                    <Chip
                      label={capability}
                      size="small"
                      className={classes.capabilityChip}
                    />
                    <Chip
                      label="SE default"
                      size="small"
                      className={classes.capabilityChip}
                      color="primary"
                    />
                  </div>
                ))}
              </FormGroup>
              <FormHelperText>
                SE users in the list automatically receive read access to these
                paths in addition to the regular policy. These capabilities are
                not configurable.
              </FormHelperText>
            </FormControl>
          )}

          {/* ── Opt-In Engine Selection (per-datacenter table) ───────────────── */}
          <div>
            <Typography variant="subtitle2" style={{ marginBottom: 8 }}>
              Additional Engines
            </Typography>
            <div style={{ overflowX: 'auto' }}>
              <table
                style={{
                  borderCollapse: 'collapse',
                  width: '100%',
                  minWidth: 380,
                }}
              >
                <thead>
                  <tr>
                    <th
                      style={{
                        textAlign: 'left',
                        padding: '4px 12px 4px 0',
                        fontSize: '0.75rem',
                        color: '#888',
                        fontWeight: 600,
                      }}
                    >
                      Datacenter
                    </th>
                    {OPT_IN_ENGINES.map(({ label }) => (
                      <th
                        key={label}
                        style={{
                          textAlign: 'center',
                          padding: '4px 8px',
                          fontSize: '0.75rem',
                          color: '#888',
                          fontWeight: 600,
                        }}
                      >
                        {label}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {values.targetClusters.map(clusterId => {
                    const dc = DATACENTERS.find(d => d.id === clusterId);
                    const clusterEngines =
                      values.enginesByCluster[clusterId] ?? [];
                    return (
                      <tr key={clusterId}>
                        <td
                          style={{
                            padding: '2px 12px 2px 0',
                            fontSize: '0.8rem',
                            whiteSpace: 'nowrap',
                          }}
                        >
                          {dc?.label ?? clusterId}
                        </td>
                        {OPT_IN_ENGINES.map(({ value }) => {
                          const supported =
                            CLUSTER_ENGINE_AVAILABILITY[clusterId]?.includes(
                              value,
                            ) ?? false;
                          const checked = clusterEngines.includes(value);
                          return (
                            <td
                              key={value}
                              style={{
                                textAlign: 'center',
                                padding: '2px 8px',
                              }}
                            >
                              <Tooltip
                                title={
                                  supported
                                    ? ''
                                    : 'Not available on this cluster'
                                }
                                placement="top"
                              >
                                <span>
                                  <Checkbox
                                    size="small"
                                    color="primary"
                                    checked={checked}
                                    disabled={
                                      !supported || values.bsns.length === 0
                                    }
                                    onChange={() =>
                                      handleEngineToggle(clusterId, value)
                                    }
                                    inputProps={{
                                      'aria-label': `${OPT_IN_ENGINES.find(e => e.value === value)?.label} — ${dc?.label ?? clusterId}`,
                                    }}
                                    style={{ padding: 4 }}
                                  />
                                </span>
                              </Tooltip>
                            </td>
                          );
                        })}
                      </tr>
                    );
                  })}
                  <tr>
                    <td
                      style={{
                        padding: '6px 12px 2px 0',
                        fontSize: '0.75rem',
                        color: '#888',
                        whiteSpace: 'nowrap',
                      }}
                    >
                      Apply to all datacenters
                    </td>
                    {OPT_IN_ENGINES.map(({ value, label }) => {
                      const engineClusters = clustersWithEngine(value);
                      return (
                        <td
                          key={value}
                          style={{ textAlign: 'center', padding: '2px 8px' }}
                        >
                          <Tooltip
                            title={
                              engineClusters.length < 2
                                ? 'Select this engine on 2+ datacenters to enable per-datacenter configuration'
                                : ''
                            }
                            placement="top"
                          >
                            <span>
                              <Switch
                                size="small"
                                color="primary"
                                checked={values.engineApplyToAll[value]}
                                disabled={
                                  engineClusters.length < 2 ||
                                  values.bsns.length === 0
                                }
                                onChange={() =>
                                  handleEngineApplyToAllToggle(value)
                                }
                                inputProps={{
                                  'aria-label': `Apply ${label} to all datacenters`,
                                }}
                              />
                            </span>
                          </Tooltip>
                        </td>
                      );
                    })}
                  </tr>
                </tbody>
              </table>
            </div>
            <Typography
              variant="caption"
              color="textSecondary"
              style={{ marginTop: 6, display: 'block' }}
            >
              Select engines per datacenter. Grayed cells are not available on
              that cluster. When "apply to all datacenters" is off, each
              datacenter gets its own configuration box below.
            </Typography>
          </div>
        </Paper>

        {/* ── PKI Section ──────────────────────────────────────────────────── */}
        {values.additionalEngines.includes('pki') &&
          (values.engineApplyToAll.pki ? (
            values.pki && (
              <Paper
                variant="outlined"
                className={classes.card}
                style={
                  isK8sDeployment
                    ? { opacity: 0.4, pointerEvents: 'none' }
                    : undefined
                }
              >
                <Typography variant="subtitle1" className={classes.cardTitle}>
                  PKI Configuration
                </Typography>
                <PkiConfigFields
                  idPrefix="pki"
                  config={values.pki}
                  onChange={patch => handlePkiChange(patch)}
                  errors={errors}
                  pkiRoleName={pkiRoleName}
                  pkiDefaultTtl={pkiDefaultTtl}
                  classes={classes}
                />
              </Paper>
            )
          ) : (
            <>
              {clustersWithEngine('pki').map(clusterId => {
                const config = values.pkiByCluster[clusterId];
                if (!config) return null;
                const dcLabel =
                  DATACENTERS.find(d => d.id === clusterId)?.label ?? clusterId;
                return (
                  <Paper
                    key={clusterId}
                    variant="outlined"
                    className={classes.card}
                    style={
                      isK8sDeployment
                        ? { opacity: 0.4, pointerEvents: 'none' }
                        : undefined
                    }
                  >
                    <Typography
                      variant="subtitle1"
                      className={classes.cardTitle}
                    >
                      PKI Configuration — {dcLabel}
                    </Typography>
                    <PkiConfigFields
                      idPrefix={`pki-${clusterId}`}
                      config={config}
                      onChange={patch => handlePkiChange(patch, clusterId)}
                      errors={errorsByCluster[clusterId] ?? {}}
                      pkiRoleName={pkiRoleName}
                      pkiDefaultTtl={pkiDefaultTtl}
                      classes={classes}
                    />
                  </Paper>
                );
              })}
            </>
          ))}

        {/* ── Database Section ─────────────────────────────────────────────── */}
        {values.additionalEngines.includes('database') &&
          availableEngines.includes('database') &&
          (values.engineApplyToAll.database ? (
            values.database && (
              <Paper
                variant="outlined"
                className={classes.card}
                style={
                  values.applicationType === 'kubernetes-workload'
                    ? { opacity: 0.4, pointerEvents: 'none' }
                    : undefined
                }
              >
                <Typography variant="subtitle1" className={classes.cardTitle}>
                  Database Configuration
                </Typography>
                <DatabaseConfigFields
                  idPrefix="db"
                  config={values.database}
                  onChange={patch => handleDatabaseChange(patch)}
                  errors={errors}
                />
              </Paper>
            )
          ) : (
            <>
              {clustersWithEngine('database').map(clusterId => {
                const config = values.databaseByCluster[clusterId];
                if (!config) return null;
                const dcLabel =
                  DATACENTERS.find(d => d.id === clusterId)?.label ?? clusterId;
                return (
                  <Paper
                    key={clusterId}
                    variant="outlined"
                    className={classes.card}
                    style={
                      values.applicationType === 'kubernetes-workload'
                        ? { opacity: 0.4, pointerEvents: 'none' }
                        : undefined
                    }
                  >
                    <Typography
                      variant="subtitle1"
                      className={classes.cardTitle}
                    >
                      Database Configuration — {dcLabel}
                    </Typography>
                    <DatabaseConfigFields
                      idPrefix={`db-${clusterId}`}
                      config={config}
                      onChange={patch => handleDatabaseChange(patch, clusterId)}
                      errors={errorsByCluster[clusterId] ?? {}}
                    />
                  </Paper>
                );
              })}
            </>
          ))}

        {/* ── AWS IAM Section ──────────────────────────────────────────────── */}
        {values.additionalEngines.includes('aws_iam') &&
          (values.engineApplyToAll.aws_iam ? (
            values.awsIam && (
              <Paper
                variant="outlined"
                className={classes.card}
                style={
                  values.applicationType === 'kubernetes-workload'
                    ? { opacity: 0.4, pointerEvents: 'none' }
                    : undefined
                }
              >
                <Typography variant="subtitle1" className={classes.cardTitle}>
                  AWS IAM Configuration
                </Typography>
                <AwsIamConfigFields
                  idPrefix="aws-iam"
                  config={values.awsIam}
                  onChange={patch => handleAwsIamChange(patch)}
                  errors={errors}
                />
              </Paper>
            )
          ) : (
            <>
              {clustersWithEngine('aws_iam').map(clusterId => {
                const config = values.awsIamByCluster[clusterId];
                if (!config) return null;
                const dcLabel =
                  DATACENTERS.find(d => d.id === clusterId)?.label ?? clusterId;
                return (
                  <Paper
                    key={clusterId}
                    variant="outlined"
                    className={classes.card}
                    style={
                      values.applicationType === 'kubernetes-workload'
                        ? { opacity: 0.4, pointerEvents: 'none' }
                        : undefined
                    }
                  >
                    <Typography
                      variant="subtitle1"
                      className={classes.cardTitle}
                    >
                      AWS IAM Configuration — {dcLabel}
                    </Typography>
                    <AwsIamConfigFields
                      idPrefix={`aws-iam-${clusterId}`}
                      config={config}
                      onChange={patch => handleAwsIamChange(patch, clusterId)}
                      errors={errorsByCluster[clusterId] ?? {}}
                    />
                  </Paper>
                );
              })}
            </>
          ))}

        {/* ── SSH CA Section ───────────────────────────────────────────────── */}
        {values.additionalEngines.includes('ssh_ca') &&
          (values.engineApplyToAll.ssh_ca ? (
            values.sshCa && (
              <Paper
                variant="outlined"
                className={classes.card}
                style={
                  values.applicationType === 'kubernetes-workload'
                    ? { opacity: 0.4, pointerEvents: 'none' }
                    : undefined
                }
              >
                <Typography variant="subtitle1" className={classes.cardTitle}>
                  SSH CA Configuration
                </Typography>
                <SshCaConfigFields
                  idPrefix="ssh-ca"
                  config={values.sshCa}
                  onChange={patch => handleSshCaChange(patch)}
                  errors={errors}
                />
              </Paper>
            )
          ) : (
            <>
              {clustersWithEngine('ssh_ca').map(clusterId => {
                const config = values.sshCaByCluster[clusterId];
                if (!config) return null;
                const dcLabel =
                  DATACENTERS.find(d => d.id === clusterId)?.label ?? clusterId;
                return (
                  <Paper
                    key={clusterId}
                    variant="outlined"
                    className={classes.card}
                    style={
                      values.applicationType === 'kubernetes-workload'
                        ? { opacity: 0.4, pointerEvents: 'none' }
                        : undefined
                    }
                  >
                    <Typography
                      variant="subtitle1"
                      className={classes.cardTitle}
                    >
                      SSH CA Configuration — {dcLabel}
                    </Typography>
                    <SshCaConfigFields
                      idPrefix={`ssh-ca-${clusterId}`}
                      config={config}
                      onChange={patch => handleSshCaChange(patch, clusterId)}
                      errors={errorsByCluster[clusterId] ?? {}}
                    />
                  </Paper>
                );
              })}
            </>
          ))}
      </div>

      {!onValuesChange && (
        <Button
          type="submit"
          variant="contained"
          color="primary"
          disabled={isSubmitting || opaDown}
        >
          {isSubmitting ? 'Submitting…' : 'Submit Request'}
        </Button>
      )}
    </form>
  );
}
